<nb-card class="dialog-card review-dialog">
  <nb-card-header>
    <h5>
      {{ "pages.dozent.participants.reviewSubmissions" | translate }}
      <span class="muted" *ngIf="exercise">— {{ exercise.title }}</span>
    </h5>
  </nb-card-header>
  <nb-card-body>
    <app-page-loader *ngIf="loading"></app-page-loader>

    <div *ngIf="!loading && submissions.length === 0" class="empty">
      {{ "pages.dozent.participants.noSubmissions" | translate }}
    </div>

    <div *ngIf="!loading && submissions.length > 0" class="review-grid">
      <!-- Sidebar: submissions list -->
      <div class="submissions-list">
        <div
          *ngFor="let s of submissions"
          class="submission-row"
          [class.selected]="selected?.id === s.id"
          (click)="selectSubmission(s)"
        >
          <div class="row-name">{{ submissionLearnerLabel(s) }}</div>
            <div class="row-meta">
              <span class="inline-badge" [class.active]="s.status === 'dozent_reviewed'">
                {{ releaseStatusKey(s) | translate }}
              </span>
              <span *ngIf="s.dozent_score != null">{{ s.dozent_score }}</span>
              <span *ngIf="s.dozent_score == null && s.ai_score != null" class="muted">
                {{ "pages.dozent.participants.aiShort" | translate }}: {{ s.ai_score }}
              </span>
            </div>
          </div>
      </div>

      <!-- Detail panel -->
      <div class="submission-detail" *ngIf="selected">
        <div class="release-status" [ngClass]="releaseStatusClass(selected)">
          <nb-icon
            [icon]="selected.feedback_released_at ? 'checkmark-circle-2-outline' : 'clock-outline'"
          ></nb-icon>
          <span>{{ releaseStatusKey(selected) | translate }}</span>
        </div>

        <div class="grading-result" *ngIf="hasStructuredReview(); else rawAnswersReview">
          <div class="result-header">
            <h6>{{ "pages.dozent.participants.resultOverview" | translate }}</h6>
            <span class="result-source-badge" [ngClass]="resultSourceClass()">
              <nb-icon icon="award-outline"></nb-icon>
              {{ resultSourceLabelKey() | translate }}
            </span>
          </div>

          <div class="score-row">
            <span class="label">{{ "pages.dozent.participants.score" | translate }}:</span>
            <span class="value">
              {{ resultScore() ?? "—" }}
              <span class="muted" *ngIf="resultMaxScore() != null">
                / {{ resultMaxScore() }}
              </span>
            </span>
          </div>

          <div class="feedback-block" *ngIf="resultFeedback()">
            <div class="label">{{ "pages.dozent.participants.feedback" | translate }}:</div>
            <p class="feedback-text">{{ resultFeedback() }}</p>
          </div>

          <div class="question-feedback-list">
            <div class="section-label">
              {{ "pages.dozent.participants.questionFeedback" | translate }}
            </div>

            <div
              class="question-feedback"
              *ngFor="let result of structuredQuestions()"
              [ngClass]="questionStatusClass(result)"
            >
              <div class="question-feedback-header">
                <div class="question-title">
                  <span class="question-number">{{ result.question_index + 1 }}.</span>
                  <span>
                    {{
                      result.question_text ||
                        getQuestionText(questionList()[result.question_index], result.question_index)
                    }}
                  </span>
                </div>
                <div class="question-score">
                  <span class="status-pill">
                    {{
                      (result.correct
                        ? "pages.dozent.participants.questionCorrect"
                        : "pages.dozent.participants.questionIncorrect"
                      ) | translate
                    }}
                  </span>
                  <span class="points">
                    {{ result.points ?? 0 }}
                    <span *ngIf="result.max_points != null">/ {{ result.max_points }}</span>
                  </span>
                </div>
              </div>

              <div class="option-review" *ngIf="hasOptionResults(result)">
                <div
                  class="review-option"
                  *ngFor="let option of result.option_results"
                  [ngClass]="optionStatusClass(option)"
                >
                  <span class="option-marker">{{ optionStatusSymbol(option) }}</span>
                  <span class="option-text">{{ option.text }}</span>
                  <span class="option-state">
                    {{ optionStatusLabelKey(option) | translate }}
                  </span>
                </div>
              </div>

              <div class="blank-review" *ngIf="hasBlankResults(result)">
                <div
                  class="blank-row"
                  *ngFor="let blank of result.blank_results"
                  [class.correct]="blank.correct"
                >
                  <span class="blank-label">
                    {{ "pages.dozent.participants.answer" | translate }} {{ (blank.index ?? 0) + 1 }}:
                  </span>
                  <span class="blank-answer">{{ blank.submitted || "—" }}</span>
                  <span class="blank-expected" *ngIf="!blank.correct">
                    {{ "pages.dozent.participants.correctAnswer" | translate }}: {{ blank.expected || "—" }}
                  </span>
                </div>
              </div>

              <div
                class="plain-answer-review"
                *ngIf="!hasOptionResults(result) && !hasBlankResults(result)"
              >
                <div *ngIf="formatValues(result.selected)">
                  <span class="mini-label">{{ "pages.dozent.participants.answer" | translate }}:</span>
                  {{ formatValues(result.selected) }}
                </div>
                <div *ngIf="formatValues(result.correct_answers)">
                  <span class="mini-label">{{ "pages.dozent.participants.correctAnswer" | translate }}:</span>
                  {{ formatValues(result.correct_answers) }}
                </div>
              </div>

              <p class="question-feedback-text" *ngIf="result.feedback">
                <span class="mini-label">{{ "pages.dozent.participants.feedback" | translate }}:</span>
                {{ result.feedback }}
              </p>
              <p class="question-explanation" *ngIf="result.explanation">
                <span class="mini-label">{{ "pages.dozent.participants.explanation" | translate }}:</span>
                {{ result.explanation }}
              </p>
            </div>
          </div>
        </div>

        <ng-template #rawAnswersReview>
          <h6>{{ "pages.dozent.participants.answers" | translate }}</h6>
          <div class="answers">
            <div class="qa" *ngFor="let q of questionList(); let i = index">
              <div class="q">
                <strong>{{ i + 1 }}.</strong>
                {{ getQuestionText(q, i) }}
              </div>
              <div class="a">{{ answerFor(q, i) || "—" }}</div>
              <div class="correct" *ngIf="getCorrectAnswer(q)">
                <span class="muted">{{ "pages.dozent.participants.correctAnswer" | translate }}:</span>
                {{ getCorrectAnswer(q) }}
              </div>
            </div>
            <div class="qa" *ngIf="questionList().length === 0">
              <em class="muted">{{ "pages.dozent.participants.noQuestionsHint" | translate }}</em>
            </div>
          </div>

          <h6>{{ "pages.dozent.participants.aiPreReview" | translate }}</h6>
          <div class="ai-block">
            <div>
              <strong>{{ "pages.dozent.participants.aiScore" | translate }}:</strong>
              {{ selected.ai_score ?? "—" }}
            </div>
            <div class="ai-feedback">{{ selected.ai_feedback || "—" }}</div>
          </div>
        </ng-template>

        <h6>{{ "pages.dozent.participants.dozentReview" | translate }}</h6>
        <div class="form-group">
          <label class="label">{{ "pages.dozent.participants.score" | translate }}</label>
          <input
            nbInput
            fullWidth
            type="number"
            [(ngModel)]="editScore"
            [placeholder]="exercise?.max_score ? ('0 - ' + exercise.max_score) : ''"
          />
        </div>
        <div class="form-group">
          <label class="label">{{ "pages.dozent.participants.feedback" | translate }}</label>
          <textarea
            nbInput
            fullWidth
            rows="4"
            [(ngModel)]="editFeedback"
          ></textarea>
        </div>
      </div>
    </div>
  </nb-card-body>
  <nb-card-footer>
    <button nbButton ghost (click)="close()">
      {{ "buttons.close" | translate }}
    </button>
    <button
      nbButton
      status="primary"
      (click)="saveFeedback()"
      [disabled]="!selected || saving"
      [nbSpinner]="saving"
      nbSpinnerSize="tiny"
      nbSpinnerStatus="control"
    >
      {{ "pages.dozent.participants.saveFeedback" | translate }}
    </button>
  </nb-card-footer>
</nb-card>
